import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Signal, SignalDetail } from '../../../../types/signals-live';
import ThreadSection from './panel/ThreadSection';
import SeveritySummaryCard from './panel/SeveritySummaryCard';
import BusinessContextCard from './panel/BusinessContextCard';
import ConductSummarySection from './panel/ConductSummarySection';
import InvestigateSection from './panel/investigate/InvestigateSection';
import ProcessingStatusPill, { ProgressInfo } from './ProcessingStatusPill';
import SignalAdminMenu from '../../shared/SignalAdminMenu';
import { StartSimulationButton } from '../../workspace/ombudsman/StartSimulationButton';
import { formatDate } from '../../../../utils/dateHelpers';
import { getSignalById } from '../../../../utils/api';
import IconWrapper from '../../../IconWrapper';
import { FiExternalLink } from 'react-icons/fi';

type SignalDetailPanelProps = {
  signal: Signal;
  detail?: SignalDetail | any;
  signalId?: string;
  progressTick: number;
  onReclassify: (signalId: string) => Promise<void>;
  onLabelTap: (meta: any) => void;
  onOpenFeedback: (signalId: string) => void;
  formatDate?: (d?: string) => string;
};

/**
 * Get dimension data checking both old and new IDs for backwards compatibility.
 * Maps dissatisfaction <-> complaint_detection
 */
const getDimensionData = (taxonomy: any, dimensionId: string): any | null => {
  if (!taxonomy?.dimensions) return null;

  // Direct lookup first
  if (taxonomy.dimensions[dimensionId]) {
    return taxonomy.dimensions[dimensionId];
  }

  // Check aliases: dissatisfaction <-> complaint_detection
  if (dimensionId === 'dissatisfaction' && taxonomy.dimensions['complaint_detection']) {
    return taxonomy.dimensions['complaint_detection'];
  }
  if (dimensionId === 'complaint_detection' && taxonomy.dimensions['dissatisfaction']) {
    return taxonomy.dimensions['dissatisfaction'];
  }

  return null;
};

/**
 * Transform taxonomy classification labels for a given dimension
 */
const transformTaxonomyLabels = (
  taxonomy: any,
  dimensionId: string,
  fallbackLabels?: string[]
): any[] => {
  const dimension = getDimensionData(taxonomy, dimensionId);

  if (!dimension) {
    // Fallback to legacy format
    if (fallbackLabels && fallbackLabels.length > 0) {
      return fallbackLabels.map((label: string) => ({
        label_name: label.replace(/_/g, ' '),
      }));
    }
    return [];
  }

  const labels: any[] = [];

  Object.entries(dimension.categories).forEach(([categoryId, category]: [string, any]) => {
    Object.entries(category.labels || {}).forEach(([labelId, label]: [string, any]) => {
      labels.push({
        label_name: label.label_name || labelId.replace(/_/g, ' '),
        rationale: label.rationale,
        confidence: label.confidence ? Math.round(label.confidence * 100) : undefined,
        regulation_reference: label.regulation_reference,
        category_name: category.category_name,
        dimension_id: dimensionId,
        category_id: categoryId,
      });
    });
  });

  return labels;
};

const SignalDetailPanel: React.FC<SignalDetailPanelProps> = ({
  signal,
  detail: initialDetail,
  signalId,
  progressTick,
  onReclassify,
  onLabelTap,
  onOpenFeedback,
  formatDate: formatDateProp,
}) => {
  const [threadOpen, setThreadOpen] = useState(false);
  const [fetchedDetail, setFetchedDetail] = useState<SignalDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const navigate = useNavigate();

  // Use provided formatDate or default from utils
  const formatDateFn = formatDateProp || formatDate;

  const handlePopOut = () => {
    navigate(`/v3/signals/${signal.id}`);
  };

  // Merge detail: prefer initialDetail (from props), then fetchedDetail, then signal
  const detail = initialDetail || fetchedDetail || (signal as any);

  // Auto-fetch detail if signalId provided but no detail available
  useEffect(() => {
    if (signalId && !initialDetail && !fetchedDetail && !loadingDetail) {
      setLoadingDetail(true);
      getSignalById(signalId)
        .then((data) => {
          setFetchedDetail(data);
        })
        .catch((err) => {
          console.error('Failed to fetch signal detail:', err);
          // Fallback to signal data, already handled by detail merge above
        })
        .finally(() => {
          setLoadingDetail(false);
        });
    }
  }, [signalId, initialDetail, fetchedDetail, loadingDetail]);

  const prog = detail?._progress as ProgressInfo;
  const status = detail?.processing_status || signal.processing_status;
  
  // Extract taxonomy classification (prefer detail, then signal)
  const taxonomy = detail?.taxonomy_classification || (signal as any).taxonomy_classification;
  const classification = detail?.classification || signal.classification;

  // Build ConductSummarySection data with helper function
  const conductSummaryData = {
    complaint: {
      detected: detail?.complaint_detected ?? signal.complaint_detected ?? true,
      explanation_short: classification?.complaint_explanation_short,
      confidence: classification?.complaint_confidence,
      labels: taxonomy
        ? transformTaxonomyLabels(taxonomy, 'dissatisfaction', classification?.complaint_category ? [classification.complaint_category] : undefined)
        : ((classification?.complaint_category ? [classification.complaint_category] : []).map((label: string) => ({
            label_name: label.replace(/_/g, ' '),
          })) as any[]),
    },
    vulnerability: {
      detected: detail?.vulnerability_detected ?? signal.vulnerability_detected ?? true,
      labels: taxonomy
        ? transformTaxonomyLabels(taxonomy, 'vulnerability', classification?.vulnerability_labels)
        : ((classification?.vulnerability_labels || []).map((label: string) => ({
            label_name: label.replace(/_/g, ' '),
          })) as any[]),
    },
    strain: {
      summary_short: classification?.strain_summary_short,
      labels: taxonomy
        ? transformTaxonomyLabels(taxonomy, 'customer_strain', classification?.strain_labels)
        : ((classification?.strain_labels || []).map((label: string) => ({
            label_name: label.replace(/_/g, ' '),
          })) as any[]),
    },
    harm: {
      summary_short: classification?.harm_summary_short,
      labels: taxonomy
        ? transformTaxonomyLabels(taxonomy, 'foreseeable_harm', classification?.harm_labels)
        : ((classification?.harm_labels || []).map((label: string) => ({
            label_name: label.replace(/_/g, ' '),
          })) as any[]),
    },
    consumer_duty: {
      summary_short: classification?.outcome_assessment || 'Not available',
      labels: taxonomy
        ? transformTaxonomyLabels(taxonomy, 'consumer_duty', classification?.consumer_duty_outcomes)
        : ((classification?.consumer_duty_outcomes || []).map((label: string) => ({
            label_name: label.replace(/_/g, ' '),
          })) as any[]),
    },
  };

  const detailThreadData = detail?.thread_data;
  const threadMessages = detail?.thread_messages ||
    (Array.isArray(detailThreadData) ? detailThreadData : detailThreadData?.messages) ||
    [];
  const customerName = detail?.customer?.name || detail?.customer_name || signal.customer?.name || signal.customer_name || 'Customer';

  return (
    <div className="pb-4">
      <div id={`signal-panel-${signal.id}`} className="rounded-2xl bg-[#fbf9f6] p-6 mt-2 border border-[#F4F4F4] shadow-[0_2px_8px_rgba(38,38,40,0.06)] relative">
        <div className="absolute top-4 right-4 z-10 flex items-center gap-2">
          <button
            onClick={handlePopOut}
            className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-go-primary"
            title="View in full page"
            aria-label="View signal in full page"
          >
            <IconWrapper icon={FiExternalLink} className="w-4 h-4" />
          </button>
          <SignalAdminMenu 
            signal={detail}
            onReclassify={onReclassify}
          />
        </div>

        <div className="text-figma-lg font-semibold text-figma-text-primary mb-2 pr-20">
          {signal.summary || detail?.summary || (signal.verbatim ? signal.verbatim.substring(0, 100) + '…' : 'Signal')}
        </div>

        <ProcessingStatusPill status={status} progress={prog} small={false} tick={progressTick} />

        {/* Meta */}
        <div className="text-figma-sm text-figma-text-secondary flex items-center gap-3 mb-4">
          <span className="text-figma-text-muted">{signal.customer?.product_policy || signal.customer_product_policy || 'Not available'}</span>
          <span className="text-figma-text-muted">•</span>
          <span className="capitalize">{(signal.channel || '').replace('_',' ') || 'Channel'}</span>
          <span className="text-figma-text-muted">•</span>
          <span>{formatDateFn(signal.received_at || signal.created_at)}</span>
        </div>
        <div className="space-y-4">
          {/* Top Row */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            <div className="lg:col-span-3">
              <SeveritySummaryCard
                conductScore={signal.conduct_score ?? signal.severity_score}
                summaryText={
                  detail?.summary_detailed ||
                  signal.summary_detailed ||
                  detail?.verbatim ||
                  signal.verbatim ||
                  classification?.outcome_assessment ||
                  signal.classification?.outcome_assessment
                }
                threadOpen={threadOpen}
                onToggleThread={() => setThreadOpen(!threadOpen)}
              />
              {threadOpen && (
                <div className="mt-3">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-gray-900 flex items-center">Customer Case Thread</h3>
                    {(() => {
                      const messageCount = Array.isArray(threadMessages) ? threadMessages.length : 0;
                      if (messageCount > 0) {
                        return <span className="text-xs text-gray-500">{messageCount} message{messageCount !== 1 ? 's' : ''}</span>;
                      }
                      return null;
                    })()}
                  </div>
                  <div className="max-h-96 overflow-y-auto bg-[#fbf9f6] rounded-xl p-4 space-y-4">
                    <ThreadSection
                      threadMessages={threadMessages}
                      customerName={customerName}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Business Context Card */}
            <div className="lg:col-span-1">
              <BusinessContextCard
                journeyStage={signal.context?.journey_stage || signal.journey_stage}
                source={signal.source_type || signal.context?.channel || signal.channel}
                productPolicy={signal.customer?.product_policy || signal.customer_product_policy}
              />
            </div>
          </div>

          <ConductSummarySection
            data={conductSummaryData}
            onLabelTap={onLabelTap}
          />

          {/* Investigate Section: Ombudsman quick insights */}
          <InvestigateSection
            signalId={signal.id}
            fosOpinion={
              (detail?.fos_opinion || classification?.fos_opinion || signal.fos_opinion || signal.classification?.fos_opinion) as any
            }
          />

          {/* Start FOS Simulation Button */}
          <div className="mt-4">
            <StartSimulationButton signalId={signal.id} variant="default" size="sm" />
          </div>

          {/* Feedback CTA */}
          {(() => {
            const canReview = !!(
              (classification?.id && taxonomy) ||
              (signal.classification?.id && (signal as any).taxonomy_classification)
            );
            if (!canReview) return null;
            return (
              <div className="mt-4 flex justify-end">
                <button onClick={() => onOpenFeedback(signal.id)} className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:text-gray-900 hover:border-gray-400">
                  Provide Feedback
                </button>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default React.memo(SignalDetailPanel);
